'use client';

import { createContext, type Dispatch, type SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import { defaultArcSpanPageConfig } from '@/app_lib/defaults';
import { DeviceSize } from '@/app_lib/hooks/useMediaQuery';
import { useUser } from '@/app_lib/providers/UserProvider';
import { AdLibrary } from '@/app_lib/services/ads/AdLibrary';
import { OutbrainLoadingState } from '@/app_lib/services/Outbrain/config';
import type { AdPlacementConfig, PageAdConfig, PlacementName } from '@/app_lib/types/Ad';
import type { ArcSpanPageConfig } from '@/app_lib/types/ArcSpan';
import { buildConfig } from '@/app_lib/utils/ads';
import { calculateBrowserWidth } from '@/app_lib/utils/clientUtils';

const usePageview = (adConsent: boolean | null): number => {
  const [pv, setPv] = useState(0);
  useEffect(() => {
    if (adConsent) {
      setPv((prev) => prev + 1);
    }
  }, [adConsent]);

  return pv;
};

interface AdProviderProps {
  children: React.ReactNode;
  hasLoggedIn: boolean;
  pageAdConfig?: PageAdConfig;
  setPageAdConfig: (config: PageAdConfig) => void;
}

interface AdContextInterface {
  arcSpanPageConfig?: ArcSpanPageConfig;
  hideInStreamAds: boolean;
  hideOutbrain: boolean;
  renderOutbrainScript: boolean;
  outbrainScriptLoadedState: OutbrainLoadingState;
  pageAdConfig?: PageAdConfig;
  setArcSpanPageConfig: (config: ArcSpanPageConfig) => void;
  setHideInStreamAds: Dispatch<SetStateAction<boolean>>;
  setHideOutbrain: Dispatch<SetStateAction<boolean>>;
  setRenderOutbrainScript: Dispatch<SetStateAction<boolean>>;
  setOutbrainScriptLoadedState: Dispatch<SetStateAction<OutbrainLoadingState>>;
  setPageAdConfig: (config: PageAdConfig) => void;
}

const defaultAdContext: AdContextInterface = {
  arcSpanPageConfig: defaultArcSpanPageConfig,
  hideInStreamAds: false,
  hideOutbrain: false,
  outbrainScriptLoadedState: OutbrainLoadingState.Pending,
  pageAdConfig: undefined,
  renderOutbrainScript: false,
  setArcSpanPageConfig: () => {},
  setHideInStreamAds: () => {},
  setHideOutbrain: () => {},
  setOutbrainScriptLoadedState: () => {},
  setPageAdConfig: () => {},
  setRenderOutbrainScript: () => {},
};

// biome-ignore lint/style/useComponentExportOnlyModules: context should be with provider
export const AdContext = createContext<AdContextInterface>(defaultAdContext);

export const AdProvider = ({ children, hasLoggedIn, pageAdConfig, setPageAdConfig }: AdProviderProps) => {
  const [hideOutbrain, setHideOutbrain] = useState<boolean>(defaultAdContext.hideOutbrain);
  const [renderOutbrainScript, setRenderOutbrainScript] = useState(defaultAdContext.renderOutbrainScript);
  const [outbrainScriptLoadedState, setOutbrainScriptLoadedState] = useState(
    defaultAdContext.outbrainScriptLoadedState,
  );
  const [arcSpanPageConfig, setArcSpanPageConfig] = useState<ArcSpanPageConfig | undefined>(
    defaultAdContext.arcSpanPageConfig,
  );
  const [hideInStreamAds, setHideInStreamAds] = useState<boolean>(defaultAdContext.hideInStreamAds);

  if (pageAdConfig) {
    pageAdConfig.loginStatus = hasLoggedIn ? 'logged-in' : 'guest';
  }

  const memoAdContext = useMemo(
    () => ({
      arcSpanPageConfig,
      hideInStreamAds,
      hideOutbrain,
      outbrainScriptLoadedState,
      pageAdConfig,
      renderOutbrainScript,
      setArcSpanPageConfig,
      setHideInStreamAds,
      setHideOutbrain,
      setOutbrainScriptLoadedState,
      setPageAdConfig,
      setRenderOutbrainScript,
    }),
    [
      arcSpanPageConfig,
      renderOutbrainScript,
      outbrainScriptLoadedState,
      hideOutbrain,
      pageAdConfig,
      hideInStreamAds,
      setPageAdConfig,
    ],
  );

  return <AdContext.Provider value={memoAdContext}>{children}</AdContext.Provider>;
};

// biome-ignore lint/style/useComponentExportOnlyModules: <context should be with provider>
export const useAdContext = () => {
  const context = useContext(AdContext);
  if (!context) {
    throw new Error('useAdContext must be used within an AdProvider');
  }
  return context;
};

// biome-ignore lint/style/useComponentExportOnlyModules: <context should be with provider>
export const useAdContextConfig = (
  pageAdConfig: PageAdConfig,
): {
  adsEnabled: boolean;
  channel: string;
  cid: string;
  ctype: string;
  getPlacementConfig: (name: PlacementName) => AdPlacementConfig;
  initialized: boolean;
  premiumCategory: string;
  tags: string[];
  topics: string[];
} => {
  const { hasConsent, personalizedAds } = useUser();

  const pv = usePageview(hasConsent);

  if (calculateBrowserWidth() < DeviceSize.Tablet) {
    pageAdConfig.defaultPath = 'fortune/mobile';
  } else {
    pageAdConfig.defaultPath = 'fortune/desktop';
  }

  if (pageAdConfig.ch === 'homepage') {
    pageAdConfig.ch = 'home';
  }

  const config = buildConfig(pageAdConfig, pv, Boolean(personalizedAds));
  const adLib = AdLibrary.getInstance();
  adLib.init(config);

  return useMemo(() => {
    return {
      adsEnabled: true,
      channel: pageAdConfig.ch,
      cid: pageAdConfig.cid || '',
      ctype: pageAdConfig.ctype,
      getPlacementConfig: (name: PlacementName) => {
        const adLibConfig = AdLibrary.getInstance();
        return adLibConfig.getPlacementConfig(name);
      },
      initialized: true,
      premiumCategory: pageAdConfig.premiumCategory || '',
      tags: pageAdConfig.tags,
      topics: pageAdConfig.topics,
    };
  }, [pageAdConfig]);
};
